// Bits every admin screen shares: chips with admin semantics, regions, settings, small formatters.
import { useQuery } from '@tanstack/react-query';
import { BROKER_REQUEST_LABELS, COMMISSION_LABELS, EVALUATION_LABELS, VIEWING_LABELS } from '@wasity/shared';
import { Chip, StatusChip } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ApiError, api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import type { BR, EV, Region, Setting, VE } from '@/lib/types';

/**
 * In the admin panel the warning colour means "needs an admin's hand" — not "someone else is waiting".
 * A new or returned request, a commission to confirm and a refund to make are warnings; an offer the
 * owner must answer or a fee the owner must pay are plain.
 */
export function AdminBrChip({ br }: { br: BR }) {
  const { t, pick } = useI18n();
  // A decline and a silent no-show both put the request back in the queue, and both used to read
  // «the deadline expired» — telling the admin a broker ignored them when he had answered with a reason.
  if (br.status === 'new' && br.returnedReason === 'declined') return <Chip form="warning">{t('status.declinedReassign')}</Chip>;
  if (br.status === 'new' && br.returnedAt) return <Chip form="warning">{t('status.expiredReassign')}</Chip>;
  if (br.status === 'new') return <Chip form="warning">{t('status.awaitingAssignment')}</Chip>;
  if (br.status === 'completed' && br.commissionStatus === 'due') return <Chip form="warning">{t('status.commissionAwaitingConfirm')}</Chip>;
  const label = BROKER_REQUEST_LABELS[br.status];
  return <Chip form={label.chip === 'warning' ? 'outlined' : label.chip}>{pick(label)}</Chip>;
}
export function AdminEvChip({ ev }: { ev: EV }) {
  const { t, pick } = useI18n();
  if (ev.status === 'new' && ev.returnedAt) return <Chip form="warning">{t('status.expiredReassign')}</Chip>;
  if (ev.status === 'new') return <Chip form="warning">{t('status.awaitingAssignment')}</Chip>;
  if (ev.status === 'fee_set') return <Chip form="outlined">{t('status.awaitingPayment')}</Chip>;
  const label = EVALUATION_LABELS[ev.status];
  return <Chip form={label.chip === 'warning' ? 'outlined' : label.chip}>{pick(label)}</Chip>;
}
export function CommissionChip({ br }: { br: BR }) {
  const { t } = useI18n();
  if (br.status !== 'completed' || br.commissionStatus === 'none') return <span className="text-muted">—</span>;
  if (br.commissionStatus === 'due') return <Chip form="warning">{t('status.commissionAwaitingConfirm')}</Chip>;
  return <StatusChip label={COMMISSION_LABELS.paid} />;
}
export function VeChip({ ve }: { ve: VE }) {
  return <StatusChip label={VIEWING_LABELS[ve.status]} />;
}
export const waitingOn = (status: VE['status']) => ({ with_broker: 'broker', sent_to_owner: 'owner', back_to_buyer: 'buyer' })[status as string] ?? null;

export function useRegions() {
  return useQuery({ queryKey: ['regions'], queryFn: () => api.get<{ items: Region[] }>('/api/public/regions'), staleTime: 5 * 60_000 });
}
export function useSettings() {
  return useQuery({ queryKey: ['admin', 'settings'], queryFn: () => api.get<{ items: Setting[] }>('/api/admin/settings'), staleTime: 60_000 });
}
export function settingValue<T>(items: Setting[] | undefined, key: string, fallback: T): T {
  const s = items?.find((i) => i.key === key);
  return s && s.value !== undefined && s.value !== null ? (s.value as T) : fallback;
}
export const ratePct = (rate: number) => `${Number((rate * 100).toFixed(2))}%`;
/**
 * App users sign in with a phone, and the API mints `<digits>@phone.wasity.local` to fill
 * the account's e-mail column. It is not an address anyone can write to: show it as missing
 * instead of a 31-character unbreakable token that pushes the users table out of its card.
 */
export const realEmail = (email: string | null | undefined) => (!email || email.endsWith('@phone.wasity.local') ? null : email);
/** Match a property's district to a region id so the candidate picker sorts by proximity. */
export const regionIdFor = (regions: Region[] | undefined, district: string) => regions?.find((r) => r.nameAr === district || r.nameEn.toLowerCase() === district.toLowerCase())?.id;

// ── Calendar days ───────────────────────────────────────────────────────────────
/** The product's calendar day for a moment: Riyadh time, `YYYY-MM-DD` — the same keys the API buckets its trends by. */
const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit' });
export const riyadhDay = (d: Date | string | number) => dayKey.format(new Date(d));
/** The last `n` Riyadh calendar days ending today, oldest first (Riyadh has no daylight-saving jumps, so 24-hour steps are safe). */
export const lastDays = (n: number, now = Date.now()) => Array.from({ length: n }, (_, i) => riyadhDay(now - (n - 1 - i) * 86_400_000));
/** Counts per day from the API's `{day, n}` rows laid on the last `n` days, missing days as 0. */
export const dailySeries = (rows: { day: string; n: number }[], n: number) => lastDays(n).map((day) => rows.find((r) => r.day === day)?.n ?? 0);

// ── Form and API errors, in words ───────────────────────────────────────────────
/** `rule` is the API's language-neutral form of the broken rule (see `apps/api/src/routes/util.ts`). */
export interface IssueRule { kind: 'required' | 'minLength' | 'maxLength' | 'minValue' | 'maxValue' | 'invalid'; n?: number }
export interface Issue { path: string; message: string; rule?: IssueRule }
/** A form refused before it reached the API: rendered exactly like the API's own 422. */
export class ValidationError extends Error {
  constructor(public readonly issues: Issue[]) {
    super('validation_failed');
  }
}
const apiIssues = (e: unknown): Issue[] | null => {
  if (e instanceof ValidationError) return e.issues;
  if (e instanceof ApiError && e.code === 'validation_failed') {
    const d = e.details as { issues?: Issue[] } | undefined;
    return d?.issues?.length ? d.issues : [{ path: '', message: e.code }];
  }
  return null;
};
/**
 * One sentence for a failed save: the validation detail field by field (a local check or the API's 422),
 * otherwise the generic line with the error code. `labels` turns a field path into the label the form shows.
 */
/** The broken rule in the UI language; zod's English sentence only when the API sent no rule. */
export function ruleText(i: Issue, t: (k: string, v?: Record<string, string | number>) => string): string {
  const r = i.rule;
  if (!r) return i.message;
  if (r.kind === 'required') return t('admin.validation.required');
  if (r.kind === 'invalid' || r.n == null) return t('admin.validation.invalid');
  return t(`admin.validation.rule.${r.kind}`, { n: r.n });
}
export function describeError(e: unknown, t: (k: string, v?: Record<string, string | number>) => string, labels: Record<string, string> = {}): string {
  const issues = apiIssues(e);
  if (issues) return `${t('admin.validation.failed')}: ${issues.map((i) => (i.path ? `${labels[i.path] ?? i.path} — ${ruleText(i, t)}` : ruleText(i, t))).join(' · ')}`;
  return errorText(e, t);
}
